import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Play,
  Sparkles,
  ChevronDown,
  CheckCircle2,
  Code2,
  ShieldCheck,
  Brain,
  Database,
  Users,
  Globe2,
  Zap,
} from "lucide-react";

import {
  herverstLogo,
  technologyIsArt,
  interswitchLogo,
  mtvLogo,
  meristemLogo,
  flautterwaveLogo,
} from "../../assets";

import gasleetlogo from "../../assets/gasleetlogo.svg";
import farmeelyLogo from "../../assets/farmeelyLogo.svg";

import secondaryBanner from "../../assets/sec-banner.png";
import secondary1Banner from "../../assets/sec12.png";

import slideImg1 from "../../assets/sliderImg1.png";
import slideImg2 from "../../assets/sliderImg2.png";
import slideImg3 from "../../assets/sliderImg3.png";
import slideImg4 from "../../assets/sliderImg4.png";

import Button from "../../components/button/button";
import Layout from "../../components/shared/layout/layout";

import ExploreOur from "./component/expolre-our";
import Faq from "./component/faq";
import HowItWorks from "./component/how-it-works";
import OurEvent from "./component/our-event";
import Testimonial from "./component/testimonial";
import WhyZulfah from "./component/why-zulfah";

import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import SwiperCore from "swiper";
import { Navigation, Pagination, Autoplay } from "swiper/modules";

SwiperCore.use([Navigation, Pagination, Autoplay]);

const images = [slideImg4, slideImg3, slideImg2, slideImg1];

const LandingPage = () => {
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowPopup(true);
    }, 8000);

    return () => clearTimeout(timer);
  }, []);

  const tracks = [
    {
      icon: Code2,
      title: "Software Engineering",
      description:
        "Build real-world digital products and develop the skills companies actually hire for.",
      color: "#59B5CD",
    },
    {
      icon: ShieldCheck,
      title: "Cybersecurity",
      description:
        "Learn how to protect systems, applications, infrastructure and digital identities.",
      color: "#CF385D",
    },
    {
      icon: Brain,
      title: "Artificial Intelligence",
      description:
        "Explore AI, machine learning and intelligent systems shaping the future.",
      color: "#ECB32C",
    },
    {
      icon: Database,
      title: "Data Engineering",
      description:
        "Learn how modern organisations collect, process and use data at scale.",
      color: "#59B5CD",
    },
  ];

  const partners = [
    gasleetlogo,
    interswitchLogo,
    farmeelyLogo,
    herverstLogo,
    meristemLogo,
    flautterwaveLogo,
  ];

  return (
    <Layout>
      {/* =====================================================
          POPUP
      ====================================================== */}

      {showPopup && (
       <div className="hidden md:fixed md:inset-0 md:z-[100] md:flex md:items-center md:justify-center md:bg-black/80 md:px-4 md:backdrop-blur-md">

          <div className="relative w-full max-w-5xl overflow-hidden rounded-[2rem] bg-white shadow-2xl">

            <button
              onClick={() => setShowPopup(false)}
              className="absolute right-5 top-5 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-black/70 text-xl text-white transition hover:bg-[#CF385D]"
            >
              ×
            </button>

            <div className="grid md:grid-cols-2">

              <div className="h-[300px] md:h-[550px]">
                <Swiper
                  spaceBetween={20}
                  slidesPerView={1}
                  loop
                  navigation
                  pagination={{ clickable: true }}
                  autoplay={{ delay: 3500 }}
                  className="h-full"
                >
                  {images.map((img, index) => (
                    <SwiperSlide key={index}>
                      <img
                        src={img}
                        alt="Zulfah Academy programme"
                        className="h-full w-full object-cover"
                      />
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>

              <div className="flex flex-col justify-between p-8 md:p-12 ">

                <div className="">

                  <span className="text-[10px] uppercase tracking-[0.35em] text-[#CF385D]">
                    Zulfah Academy
                  </span>

                  <h2 className="mt-5 font-[ubuntu] text-4xl font-bold leading-[0.95] tracking-[-0.05em]">
                    Start your
                    <br />
                    <span className="text-[#59B5CD]">
                      technology journey.
                    </span>
                  </h2>

                  <p className="mt-6 text-sm leading-7 text-gray-500">
                    Designed for JAMBites and school leavers who want to
                    discover technology, develop practical skills and prepare
                    for the digital economy.
                  </p>

                  <div className="mt-7 grid grid-cols-2 gap-3">

                    {[
                      "Software Development",
                      "UI/UX Design",
                      "Data Analytics",
                      "Cybersecurity",
                      "AI Fundamentals",
                      "Digital Skills",
                    ].map((item) => (
                      <div
                        key={item}
                        className="flex items-center gap-2 text-xs text-gray-600"
                      >
                        <CheckCircle2 className="h-4 w-4 text-[#59B5CD]" />
                        {item}
                      </div>
                    ))}

                  </div>

                </div>

                <div className="mt-10 flex flex-wrap gap-3">

                  <a
                    href="https://docs.google.com/forms/d/e/1FAIpQLSfUuvrbrRAHm-fp1O0cJlnlSO21akZDgR7Kt3i1piufsdJi_g/viewform"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button btnText="Enroll Now" />
                  </a>

                  <button
                    onClick={() => setShowPopup(false)}
                    className="rounded-xl border border-gray-200 px-6 py-3 text-sm transition hover:border-black"
                  >
                    Maybe later
                  </button>

                </div>

              </div>

            </div>
          </div>
        </div>
      )}

      {/* =====================================================
          HERO
      ====================================================== */}

      <section className="relative min-h-[calc(100vh-80px)] overflow-hidden bg-[#080B10] text-white">

        {/* Animated background */}

        <div className="pointer-events-none absolute inset-0">

          <div className="absolute -right-40 -top-40 h-[600px] w-[600px] rounded-full bg-[#59B5CD]/15 blur-[140px]" />

          <div className="absolute -bottom-60 -left-40 h-[600px] w-[600px] rounded-full bg-[#CF385D]/10 blur-[140px]" />

          <div className="absolute left-[40%] top-[35%] h-[400px] w-[400px] rounded-full bg-[#ECB32C]/5 blur-[130px]" />

          <div
            className="absolute inset-0 opacity-[0.035]"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,.8) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.8) 1px, transparent 1px)",
              backgroundSize: "70px 70px",
            }}
          />

        </div>

        {/* Giant word */}

        <div className="pointer-events-none absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 whitespace-nowrap font-[ubuntu] text-[24vw] font-black tracking-[-0.1em] text-white/[0.025] lg:block">
          ACADEMY
        </div>

        <div className="relative mx-auto flex min-h-[calc(100vh-80px)] max-w-[1500px] flex-col justify-between px-6 py-14 sm:px-8 md:px-12 lg:px-20">

          {/* top label */}

          <div className="flex items-center justify-between">

            <div className="flex items-center gap-3">

              <span className="h-2 w-2 animate-pulse rounded-full bg-[#59B5CD]" />

              <span className="text-[9px] uppercase tracking-[0.4em] text-white/40">
                Zulfah Academy
              </span>

            </div>

            <span className="hidden text-[9px] uppercase tracking-[0.35em] text-white/20 md:block">
              Learn · Build · Become
            </span>

          </div>

          {/* hero content */}

          <div className="py-20">

            <div className="max-w-6xl">

              <div className="mb-7 flex items-center gap-3">

                <Sparkles className="h-4 w-4 text-[#ECB32C]" />

                <span className="text-[10px] uppercase tracking-[0.35em] text-[#ECB32C]">
                  The future belongs to the prepared
                </span>

              </div>

              <h1 className="font-[ubuntu] text-[58px] font-bold leading-[0.82] tracking-[-0.075em] sm:text-[80px] md:text-[110px] lg:text-[145px]">

                Learn skills.

                <br />

                <span className="text-white/20">
                  Build things.
                </span>

                <br />

                <span className="text-[#59B5CD]">
                  Change your future.
                </span>

              </h1>

              <div className="mt-10 flex max-w-3xl flex-col gap-8 md:flex-row md:items-center">

                <p className="text-base leading-8 text-white/45 md:text-lg">
                  Practical technology education for people who want more than
                  certificates. Learn by building, work with real tools and
                  develop skills that can take you anywhere.
                </p>

                <div className="hidden h-px w-20 bg-white/20 md:block" />

              </div>

              <div className="mt-10 flex flex-wrap gap-4">

                <Link
                  to="/get-started"
                  className="group inline-flex items-center gap-4 bg-[#ECB32C] px-7 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-black transition hover:-translate-y-1 hover:bg-[#59B5CD]"
                >
                  Start learning

                  <ArrowUpRight className="h-4 w-4 transition group-hover:translate-x-1 group-hover:-translate-y-1" />
                </Link>

                <a
                  href="#programmes"
                  className="group inline-flex items-center gap-4 border border-white/15 px-7 py-4 text-xs font-medium uppercase tracking-[0.2em] text-white/80 transition hover:border-white/40"
                >
                  Explore programmes

                  <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
                </a>

              </div>

            </div>

          </div>

          {/* bottom stats */}

          <div className="grid grid-cols-2 border-t border-white/10 md:grid-cols-4">

            {[
              ["500+", "Students trained"],
              ["4+", "Years of impact"],
              ["10+", "Countries reached"],
              ["8+", "Hiring partners"],
            ].map(([number, label]) => (
              <div
                key={label}
                className="border-r border-white/10 py-7 last:border-r-0"
              >
                <div className="font-[ubuntu] text-3xl font-bold">
                  {number}
                </div>

                <div className="mt-2 text-[9px] uppercase tracking-[0.2em] text-white/30">
                  {label}
                </div>
              </div>
            ))}

          </div>

        </div>
      </section>

      {/* =====================================================
          TRUST STRIP
      ====================================================== */}

      <section className="border-b border-black/10 bg-white px-6 py-10">

        <div className="mx-auto max-w-[1400px]">

          <div className="mb-7 text-center">

            <span className="text-[9px] uppercase tracking-[0.35em] text-gray-400">
              Our learners go places
            </span>

          </div>

          <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-8 opacity-60 grayscale transition duration-500 hover:grayscale-0">

            {partners.map((logo, index) => (
              <img
                key={index}
                src={logo}
                alt="Zulfah Academy partner"
                className="h-7 max-w-[110px] object-contain md:h-9"
              />
            ))}

          </div>

        </div>

      </section>

      {/* =====================================================
          INTRO
      ====================================================== */}

      <section className="relative overflow-hidden bg-[#F7F7F5] px-6 py-28 sm:px-8 md:px-12 md:py-36 lg:px-20">

        <div className="absolute right-[-150px] top-20 h-[400px] w-[400px] rounded-full bg-[#59B5CD]/10 blur-[120px]" />

        <div className="relative mx-auto max-w-[1400px]">

          <div className="grid gap-16 lg:grid-cols-[0.7fr_1.3fr]">

            <div>

              <span className="text-[9px] uppercase tracking-[0.4em] text-[#CF385D]">
                01 · Why Zulfah Academy
              </span>

              <h2 className="mt-7 font-[ubuntu] text-5xl font-bold leading-[0.88] tracking-[-0.06em] md:text-7xl">

                Education
                <br />

                <span className="text-gray-300">
                  that moves.
                </span>

              </h2>

            </div>

            <div>

              <p className="font-[ubuntu] text-3xl leading-[1.05] tracking-[-0.04em] md:text-5xl">

                We don't just teach technology.

                <span className="text-gray-300">
                  {" "}We help people become capable of building with it.
                </span>

              </p>

              <p className="mt-8 max-w-2xl text-sm leading-8 text-gray-500 md:text-base">
                Our programmes combine structured learning, practical
                projects, mentorship and exposure to the tools used by modern
                technology teams.
              </p>

            </div>

          </div>

        </div>

      </section>

      {/* =====================================================
          PROGRAMMES
      ====================================================== */}

      <section
        id="programmes"
        className="bg-[#080B10] px-6 py-28 text-white sm:px-8 md:px-12 md:py-36 lg:px-20"
      >

        <div className="mx-auto max-w-[1400px]">

          <div className="mb-16 flex flex-col justify-between gap-10 md:flex-row md:items-end">

            <div>

              <span className="text-[9px] uppercase tracking-[0.4em] text-[#59B5CD]">
                02 · What you can learn
              </span>

              <h2 className="mt-7 font-[ubuntu] text-5xl font-bold leading-[0.88] tracking-[-0.06em] md:text-7xl">

                Skills for the
                <br />

                <span className="text-white/20">
                  digital economy.
                </span>

              </h2>

            </div>

            <p className="max-w-md text-sm leading-7 text-white/40">
              Choose a path, develop practical skills and build the confidence
              to solve real-world problems.
            </p>

          </div>

          <div className="grid gap-3 md:grid-cols-2">

            {tracks.map((track, index) => {

              const Icon = track.icon;

              return (
                <div
                  key={track.title}
                  className="group relative overflow-hidden border border-white/10 bg-white/[0.03] p-8 transition duration-500 hover:-translate-y-2 hover:bg-white/[0.06] md:p-12"
                >

                  <div
                    className="absolute left-0 top-0 h-1 w-0 transition-all duration-500 group-hover:w-full"
                    style={{ backgroundColor: track.color }}
                  />

                  <div className="flex items-start justify-between">

                    <span
                      className="text-[10px]"
                      style={{ color: track.color }}
                    >
                      0{index + 1}
                    </span>

                    <Icon
                      className="h-6 w-6 text-white/20 transition group-hover:text-white"
                      style={{
                        color: undefined,
                      }}
                    />

                  </div>

                  <h3 className="mt-20 font-[ubuntu] text-3xl font-semibold tracking-[-0.04em] md:text-4xl">
                    {track.title}
                  </h3>

                  <p className="mt-5 max-w-lg text-sm leading-7 text-white/40 transition group-hover:text-white/60">
                    {track.description}
                  </p>

                  <div className="mt-8 flex items-center gap-3 text-[9px] uppercase tracking-[0.25em] text-white/30 transition group-hover:text-white">
                    Explore programme
                    <ArrowUpRight className="h-4 w-4" />
                  </div>

                </div>
              );
            })}

          </div>

        </div>

      </section>

      {/* =====================================================
          WHY ZULFAH EXISTING COMPONENT
      ====================================================== */}

      <WhyZulfah />

      {/* =====================================================
          BIG STATEMENT
      ====================================================== */}

      <section className="relative overflow-hidden bg-[#59B5CD] px-6 py-32 text-white sm:px-8 md:px-12 md:py-44 lg:px-20">

        <div className="pointer-events-none absolute right-[-100px] top-[-150px] h-[500px] w-[500px] rounded-full bg-white/10 blur-[120px]" />

        <div className="relative mx-auto max-w-[1400px]">

          <span className="text-[9px] uppercase tracking-[0.4em] text-white/50">
            03 · Our philosophy
          </span>

          <h2 className="mt-8 max-w-6xl font-[ubuntu] text-5xl font-bold leading-[0.88] tracking-[-0.065em] sm:text-6xl md:text-8xl">

            Your background
            <br />

            should not decide
            <br />

            <span className="text-white/40">
              your future.
            </span>

          </h2>

          <p className="mt-10 max-w-2xl text-sm leading-8 text-white/70 md:text-base">
            With the right knowledge, mentorship and opportunity, people can
            build careers that once seemed impossible.
          </p>

        </div>

      </section>

      {/* =====================================================
          EXPLORE EXISTING
      ====================================================== */}

      <ExploreOur />

      {/* =====================================================
          HOW IT WORKS
      ====================================================== */}

      <HowItWorks />

      {/* =====================================================
          VISUAL STATEMENT
      ====================================================== */}

      <section className="px-4 py-20 sm:px-6 md:px-10 lg:px-14">

        <div className="group relative mx-auto max-w-[1500px] overflow-hidden">

          <img
            src={technologyIsArt}
            alt="Technology is art"
            className="h-[500px] w-full transition duration-[1200ms] group-hover:scale-[1.02] md:h-[700px]"
          />

          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />

          <div className="absolute bottom-8 left-8 md:bottom-16 md:left-16">

            <span className="text-[9px] uppercase tracking-[0.4em] text-white/40">
              The Zulfah mindset
            </span>

            <h2 className="mt-5 font-[ubuntu] text-4xl font-bold leading-[0.9] tracking-[-0.05em] text-white md:text-7xl">

              Technology is
              <br />

              <span className="text-white/50">
                something you create.
              </span>

            </h2>

          </div>

        </div>

      </section>

      {/* =====================================================
          OUTCOMES
      ====================================================== */}

      <section className="bg-white px-6 py-28 sm:px-8 md:px-12 md:py-36 lg:px-20">

        <div className="mx-auto max-w-[1400px]">

          <div className="grid gap-16 lg:grid-cols-2">

            <div>

              <span className="text-[9px] uppercase tracking-[0.4em] text-[#ECB32C]">
                04 · What you leave with
              </span>

              <h2 className="mt-7 font-[ubuntu] text-5xl font-bold leading-[0.88] tracking-[-0.06em] md:text-7xl">

                More than
                <br />

                <span className="text-gray-200">
                  a certificate.
                </span>

              </h2>

            </div>

            <div className="grid gap-3 sm:grid-cols-2">

              {[
                ["Practical skills", Code2],
                ["Real projects", Zap],
                ["Mentorship", Users],
                ["Career exposure", Globe2],
              ].map(([title, Icon], index) => (

                <div
                  key={title}
                  className="border border-black/10 bg-[#F7F7F5] p-8 transition hover:-translate-y-1 hover:border-black/20"
                >

                  <Icon className="h-5 w-5 text-[#59B5CD]" />

                  <h3 className="mt-12 font-[ubuntu] text-xl font-semibold">
                    {title}
                  </h3>

                  <span className="mt-3 block text-[9px] uppercase tracking-[0.2em] text-gray-300">
                    0{index + 1}
                  </span>

                </div>

              ))}

            </div>

          </div>

        </div>

      </section>

      {/* =====================================================
          TESTIMONIAL
      ====================================================== */}

      <Testimonial />

      {/* =====================================================
          EVENTS
      ====================================================== */}

      <OurEvent />

      {/* =====================================================
          FINAL CTA
      ====================================================== */}

      <section className="relative overflow-hidden bg-[#080B10] px-6 py-32 text-center text-white sm:px-8 md:py-48">

        <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap font-[ubuntu] text-[25vw] font-black tracking-[-0.1em] text-white/[0.025]">
          ZULFAH
        </div>

        <div className="relative mx-auto max-w-5xl">

          <span className="text-[9px] uppercase tracking-[0.4em] text-[#59B5CD]">
            Your next chapter starts here
          </span>

          <h2 className="mt-8 font-[ubuntu] text-6xl font-bold leading-[0.82] tracking-[-0.075em] sm:text-7xl md:text-9xl">

            Don't just
            <br />

            <span className="text-white/20">
              watch the future.
            </span>

            <br />

            Build it.

          </h2>

          <p className="mx-auto mt-10 max-w-xl text-sm leading-7 text-white/40 md:text-base">
            Start your journey with Zulfah Academy and develop the skills,
            confidence and experience to create what comes next.
          </p>

          <div className="mt-10 flex flex-wrap justify-center gap-4">

            <Link
              to="https://wa.me/2348115371014?text=Hello Zulfah Team, I want to get started in my journey into tech."
               target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-4 bg-[#ECB32C] px-8 py-5 text-[10px] font-semibold uppercase tracking-[0.25em] text-black transition hover:-translate-y-1 hover:bg-[#59B5CD]"
            >
              Start your journey

              <ArrowUpRight className="h-4 w-4 transition group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>

            <Link
              to="/get-started"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-4 border border-white/15 px-8 py-5 text-[10px] uppercase tracking-[0.25em] text-white/70 transition hover:border-white/40 hover:text-white"
            >
              Apply now

              <ArrowRight className="h-4 w-4" />
            </Link>

          </div>

        </div>

      </section>

      {/* =====================================================
          FAQ
      ====================================================== */}

      <Faq />

    </Layout>
  );
};

export default LandingPage;